import React, { useState, useEffect, useRef } from 'react';
// import { logger } from "../../utils/logger";
import { Dialog, DialogContent, DialogActions, Button, Box, Typography, IconButton, Paper, Fade, Slide } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/auth/authContext';
import { useSubscription } from '../../hooks/useSubscription';
import { advertisementService } from '../../services/advertisementService';
import { getProxyImageUrl } from '../../utils/imageUtils';
import { sanitizeAdHtmlSafe } from '../../utils/adSanitizer';
import { logger } from "../../utils/logger";
import { safeAssetUrlForImgOrHref } from '../../utils/safeAssetUrlForDom';
import VisuallyHiddenText from './VisuallyHiddenText';
import { useOverlayAdCoordinator } from '../../ads/OverlayAdCoordinator';
import { acquireOverlayWhenAvailable } from '../../ads/acquireOverlayWhenAvailable';

interface PopupAdvertisementProps {
    onClose?: () => void;
}
const PopupAdvertisement: React.FC<PopupAdvertisementProps> = ({ onClose }) => {
    const [ad, setAd] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const { user } = useAuth();
    const { showAds, isLoading: subscriptionLoading } = useSubscription();
    const { release } = useOverlayAdCoordinator();
    const claimIdRef = useRef(`apt-popup-${Date.now()}`);
    const abortRef = useRef({ aborted: false });

    useEffect(() => {
        // Don't show popup ads for premium users
        if (!showAds || subscriptionLoading) {
            setLoading(false);
            return;
        }
        let isMounted = true;
        let delayTimer: ReturnType<typeof setTimeout> | null = null;
        const signal = { aborted: false };
        abortRef.current = signal;
        const loadPopupAd = async () => {
            try {
                const result = await advertisementService.serveAdvertisements({
                    placement: 'popup',
                    user_id: user?.id,
                    session_id: advertisementService.generateSessionId(),
                    page_url: window.location.href,
                    user_agent: navigator.userAgent,
                    user_type: showAds ? 'free' : 'premium',
                    device: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop',
                    page_type: 'general'
                });
                if (isMounted && result.success && result.ads && result.ads.length > 0) {
                    setAd(result.ads[0]);
                    const delay = Math.random() * 10000 + 5000;
                    delayTimer = setTimeout(() => {
                        void (async () => {
                            if (!isMounted || !showAds || signal.aborted) {
                                return;
                            }
                            const claimId = claimIdRef.current;
                            const ok = await acquireOverlayWhenAvailable('apt_popup', claimId, {
                                signal,
                                timeoutMs: 45_000,
                            });
                            if (!isMounted || !ok || !showAds || signal.aborted) {
                                return;
                            }
                            setOpen(true);
                        })();
                    }, delay);
                }
                else if (result.error) {
                    logger.error('Advertisement service returned error:', result.error, {});
                }
            }
            catch (error) {
                logger.error('Error loading popup ad:', error, {});
            }
            finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };
        void loadPopupAd();
        return () => {
            isMounted = false;
            signal.aborted = true;
            if (delayTimer) {
                clearTimeout(delayTimer);
            }
        };
    }, [showAds, subscriptionLoading, user?.id]);

    const handleClose = () => {
        setOpen(false);
        release('apt_popup', claimIdRef.current);
        onClose?.();
    };
    useEffect(() => {
        return () => {
            release('apt_popup', claimIdRef.current);
        };
    }, [release]);

    const renderAdContent = () => {
        if (!ad)
            return null;
        const safeLink = safeAssetUrlForImgOrHref(ad.link_url);
        switch (ad.content_type) {
            case 'html':
                return (<div dangerouslySetInnerHTML={{ __html: sanitizeAdHtmlSafe(ad.content) }} style={{ maxWidth: '100%', overflow: 'hidden' }}/>);
            case 'image':
                if (!safeLink) {
                    return (<Box component="img" src={getProxyImageUrl(ad.image_url)} alt={ad.title} sx={{
                            width: '100%',
                            maxWidth: 400,
                            height: 'auto',
                            borderRadius: 1,
                        }}/>);
                }
                return (<Box component="a" href={safeLink} target="_blank" rel="noopener noreferrer" onClick={() => {
                        void advertisementService.trackAdClick(ad.id);
                        handleClose();
                    }} sx={{
                        width: '100%',
                        maxWidth: 400,
                        display: 'block',
                        borderRadius: 1,
                        cursor: 'pointer'
                    }}>
          <VisuallyHiddenText>{(ad.title && String(ad.title).trim()) || 'Sponsored advertisement'}</VisuallyHiddenText>
          <Box component="img" src={getProxyImageUrl(ad.image_url)} alt={ad.title} sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: 1
                    }}/>
        </Box>);
            case 'text':
                return (<Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {ad.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {ad.description}
            </Typography>
            <Button variant="contained" color="primary" sx={{ mt: 2 }} component={safeLink ? 'a' : 'button'} href={safeLink || undefined} target={safeLink ? '_blank' : undefined} rel={safeLink ? 'noopener noreferrer' : undefined} onClick={() => {
                void advertisementService.trackAdClick(ad.id);
                handleClose();
            }}>
              Learn More
            </Button>
          </Box>);
            default:
                return (<Typography variant="body2" color="text.secondary">
            Advertisement content not available
          </Typography>);
        }
    };
    if (loading || !ad || !showAds) {
        return null;
    }
    return (<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionComponent={Slide} transitionDuration={300} PaperProps={{
            sx: {
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                position: 'relative'
            }
        }}>
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        <IconButton
          onClick={handleClose}
          aria-label="Close advertisement"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': {
                bgcolor: 'rgba(255,255,255,1)'
            }
        }}>
          <CloseIcon aria-hidden />
        </IconButton>
        
        <Paper elevation={0} sx={{
            p: 3,
            minHeight: 200,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
          {renderAdContent()}
        </Paper>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={handleClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>);
};
export default PopupAdvertisement;
